import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary, formatDate } from "@/lib/constants";
import { toast } from "sonner";
import { Megaphone, Paperclip, Send, X, Clock, Facebook, Instagram, ImageIcon } from "lucide-react";

const AUDIENCE = [
  { key: "dsa", label: "All DSAs" },
  { key: "master_dsa", label: "All Master DSAs" },
  { key: "sales_manager", label: "All Sales Managers" },
  { key: "agent", label: "All Agents" },
  { key: "admin", label: "All Admins" },
];

export default function Marketing() {
  const [tab, setTab] = useState("email");
  const [audience, setAudience] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sendAt, setSendAt] = useState("");
  const [sending, setSending] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [caption, setCaption] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [toFb, setToFb] = useState(true);
  const [toIg, setToIg] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const publish = async () => {
    if (!caption.trim()) { toast.error("Enter a message/caption"); return; }
    if (!toFb && !toIg) { toast.error("Select Facebook and/or Instagram"); return; }
    setPublishing(true);
    try {
      const r = await api.post("/meta/publish", { caption, image_url: imageUrl, publish_facebook: toFb, publish_instagram: toIg });
      const parts = [];
      if (r.data.facebook) parts.push("Facebook");
      if (r.data.instagram) parts.push("Instagram");
      toast.success(`Published to ${parts.join(" & ")}`);
      setCaption(""); setImageUrl("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setPublishing(false);
  };

  const load = useCallback(() => api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const toggle = (k) => setAudience((a) => (a.includes(k) ? a.filter((x) => x !== k) : [...a, k]));

  const onFiles = (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => setAttachments((a) => [...a, { name: f.name, data: reader.result }]);
      reader.readAsDataURL(f);
    });
  };

  const send = async () => {
    if (!audience.length) { toast.error("Select at least one audience"); return; }
    if (!subject.trim()) { toast.error("Enter a subject"); return; }
    setSending(true);
    try {
      const payload = { subject, body, audience, attachments, send_at: sendAt ? new Date(sendAt).toISOString() : "" };
      const r = await api.post("/campaigns", payload);
      if (r.data.status === "scheduled") toast.success(`Scheduled for ${r.data.recipients} recipient(s)`);
      else toast.success(`Sent to ${r.data.sent}/${r.data.recipients} recipient(s)`);
      setSubject(""); setBody(""); setAttachments([]); setSendAt(""); setAudience([]); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setSending(false);
  };

  return (
    <div className="space-y-6" data-testid="marketing-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight flex items-center gap-2"><Megaphone size={26} className="text-emerald-600" /> Marketing Campaigns</h1>
        <p className="text-sm text-slate-500 mt-1">Send email campaigns to your network, or publish posts to Facebook &amp; Instagram.</p>
      </div>

      <div className="flex gap-2 border-b border-slate-200" data-testid="marketing-tabs">
        <button data-testid="tab-email-campaigns" onClick={() => setTab("email")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "email" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>Email Campaigns</button>
        <button data-testid="tab-social-post" onClick={() => setTab("social")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "social" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>Social Post (FB / Instagram)</button>
      </div>

      {tab === "social" && (
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-4" data-testid="social-post-panel">
        <div>
          <label className={labelCls}>Publish to</label>
          <div className="flex flex-wrap gap-2 mt-1">
            <button type="button" data-testid="target-facebook" onClick={() => setToFb((v) => !v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors inline-flex items-center gap-1.5 ${toFb ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}><Facebook size={13} /> Facebook Page</button>
            <button type="button" data-testid="target-instagram" onClick={() => setToIg((v) => !v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors inline-flex items-center gap-1.5 ${toIg ? "bg-pink-600 text-white border-pink-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}><Instagram size={13} /> Instagram</button>
          </div>
        </div>
        <div><label className={labelCls}>Message / Caption</label><textarea data-testid="social-caption" rows={6} value={caption} onChange={(e) => setCaption(e.target.value)} className={inputCls} placeholder="Write your post…" /></div>
        <div>
          <label className={labelCls}><span className="inline-flex items-center gap-1"><ImageIcon size={13} /> Image URL {toIg ? "(required for Instagram)" : "(optional)"}</span></label>
          <input data-testid="social-image-url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className={inputCls} placeholder="https://… (publicly accessible image)" />
          {toIg && <p className="text-xs text-slate-400 mt-1">Instagram posts require a public HTTPS image — Meta fetches it from this URL.</p>}
        </div>
        <div className="flex items-center gap-4">
          <button data-testid="social-publish-button" onClick={publish} disabled={publishing} className={btnPrimary}>
            <Send size={16} /> {publishing ? "Publishing…" : "Publish Now"}
          </button>
          <p className="text-xs text-slate-400">Connect your Meta credentials under Meta Integration settings first.</p>
        </div>
      </div>
      )}

      {tab === "email" && (<>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
        <div>
          <label className={labelCls}>Audience</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {AUDIENCE.map((a) => (
              <button key={a.key} type="button" data-testid={`audience-${a.key}`} onClick={() => toggle(a.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${audience.includes(a.key) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{a.label}</button>
            ))}
          </div>
        </div>
        <div><label className={labelCls}>Subject</label><input data-testid="campaign-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} /></div>
        <div><label className={labelCls}>Message</label><textarea data-testid="campaign-body" rows={7} value={body} onChange={(e) => setBody(e.target.value)} className={inputCls} placeholder="Write your marketing message…" /></div>
        <div>
          <label className={labelCls}>Attachments</label>
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium cursor-pointer" data-testid="campaign-attach-label">
            <Paperclip size={15} /> Add files
            <input type="file" multiple className="hidden" data-testid="campaign-attach-input" onChange={onFiles} />
          </label>
          <div className="flex flex-wrap gap-2 mt-2">
            {attachments.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg px-2 py-1">
                {f.name}<button onClick={() => setAttachments((a) => a.filter((_, x) => x !== i))} className="hover:text-rose-600"><X size={12} /></button>
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div><label className={labelCls}>Schedule for (optional)</label><input data-testid="campaign-schedule" type="datetime-local" value={sendAt} onChange={(e) => setSendAt(e.target.value)} className={inputCls} /></div>
          <button data-testid="campaign-send" onClick={send} disabled={sending} className={btnPrimary}>
            {sendAt ? <Clock size={16} /> : <Send size={16} />} {sending ? "Working…" : (sendAt ? "Schedule" : "Send Now")}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead><tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="text-left px-4 py-3">Subject</th><th className="text-left px-4 py-3">Audience</th><th className="text-right px-4 py-3">Recipients</th><th className="text-right px-4 py-3">Sent</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">When</th>
          </tr></thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id} data-testid={`campaign-row-${c.id}`} className="border-b border-slate-100 text-sm text-slate-700">
                <td className="px-4 py-3 font-medium text-slate-900">{c.subject}</td>
                <td className="px-4 py-3 text-xs">{(c.audience || []).join(", ")}</td>
                <td className="px-4 py-3 text-right">{c.recipient_count}</td>
                <td className="px-4 py-3 text-right">{c.sent_count}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${c.status === "sent" ? "bg-emerald-100 text-emerald-700 border-emerald-300" : c.status === "scheduled" ? "bg-blue-50 text-blue-700 border-blue-200" : c.status === "failed" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{c.status}</span></td>
                <td className="px-4 py-3 text-xs text-slate-500">{c.status === "scheduled" ? formatDate(c.send_at) : formatDate(c.sent_at || c.created_at)}</td>
              </tr>
            ))}
            {campaigns.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400 text-sm">No campaigns yet.</td></tr>}
          </tbody>
        </table>
      </div>
      </>)}
    </div>
  );
}
